import { isoWeekStart, localDateString } from './pactAutoCheckIn';

/**
 * Pact direction semantics (②e locked §0.2): the three pact types ride the
 * EXISTING pact_type enum, renamed by direction — who picks the action, who
 * does it, who watches. Everything hangs off the derived "doer side(s)":
 *
 * - mutual     → Mutual         — both sides have an action, both do.
 * - mentorship → Coaching       — the creator (coach) defines the action;
 *                                 the PARTNER is the doer.
 * - one_way    → Accountability — the creator picks their OWN action;
 *                                 the partner is the witness.
 */

export type PactType = 'mutual' | 'one_way' | 'mentorship';
export type PactSide = 'creator' | 'partner';

export const PACT_TYPE_LABELS: Record<PactType, string> = {
  mutual: 'Mutual',
  mentorship: 'Coaching',
  one_way: 'Accountability',
};

/** Display label for a pact type; unknown values pass through unchanged. */
export function pactTypeLabel(type: string): string {
  return PACT_TYPE_LABELS[type as PactType] ?? type;
}

/** Create-modal cards, in presentation order (creator's perspective). */
export const PACT_TYPE_CHOICES: { value: PactType; label: string; desc: string }[] = [
  { value: 'mutual', label: 'Mutual', desc: 'You both commit to an action' },
  { value: 'mentorship', label: 'Coaching', desc: 'They do it — you set the action and coach' },
  { value: 'one_way', label: 'Accountability', desc: 'You do it — they hold you to it' },
];

/** Which side(s) DO the action. */
export function doerSides(type: PactType): PactSide[] {
  switch (type) {
    case 'mutual':
      return ['creator', 'partner'];
    case 'mentorship':
      return ['partner'];
    case 'one_way':
      return ['creator'];
  }
}

/** The side `userId` is on, or null for non-parties. */
export function pactSideOf(
  pact: { creatorId: string; partnerId: string },
  userId: string | null | undefined
): PactSide | null {
  if (!userId) return null;
  if (pact.creatorId === userId) return 'creator';
  if (pact.partnerId === userId) return 'partner';
  return null;
}

export function isDoer(type: PactType, side: PactSide): boolean {
  return doerSides(type).includes(side);
}

/** True when `side`'s counterpart is a doer — i.e. there is someone to nudge. */
export function canNudgeSide(type: PactType, side: PactSide): boolean {
  return isDoer(type, side === 'creator' ? 'partner' : 'creator');
}

/** One-line direction copy from the viewer's seat. */
export function directionCopy(
  type: PactType,
  mySide: PactSide | null,
  partnerName = 'your partner'
): string {
  if (type === 'mutual') return `You and ${partnerName} both do this`;
  if (type === 'mentorship') {
    return mySide === 'partner'
      ? `You do this — ${partnerName} coaches you`
      : `${partnerName} does this — you coach`;
  }
  // one_way (Accountability)
  return mySide === 'partner'
    ? `${partnerName} does this — you hold them accountable`
    : `You do this — ${partnerName} holds you accountable`;
}

/** Invite copy (pending pact, partner's seat) — per-type plain language. */
export function pactInviteCopy(type: PactType, senderName = 'Your partner'): string {
  if (type === 'mutual') {
    return `${senderName} invited you to a Mutual pact — you'll both commit to an action and keep each other honest.`;
  }
  if (type === 'mentorship') {
    return `${senderName} invited you to a Coaching pact — they set the action, you do it, they coach you along.`;
  }
  return `${senderName} invited you to an Accountability pact — they do the action, you witness and encourage.`;
}

/**
 * Hard delete is allowed only for no-history pacts (locked §0.11): pending,
 * or cancelled/declined with zero check-ins. Mirrors the DELETE policy —
 * the server is the truth, this just gates the UI.
 */
export function canDeletePact(pact: { status: string; totalCheckIns: number }): boolean {
  return (
    (pact.status === 'pending' || pact.status === 'cancelled') && pact.totalCheckIns === 0
  );
}

/**
 * Nudge rate limit (locked §0.9): once per cadence period per pact.
 * Daily/custom cadences = same local day; weekly = same ISO week (Mon–Sun).
 */
export function isSameCadencePeriod(cadence: string, a: Date, b: Date): boolean {
  if (cadence === 'weekly') {
    return isoWeekStart(a).getTime() === isoWeekStart(b).getTime();
  }
  return localDateString(a) === localDateString(b);
}
